import { z } from 'zod'
import { baseEntitySchema } from '../entity.js'
import { colorNames } from '../../lib/color.js'

const GB = 1024 * 1024 * 1024
const storageQuota = 1 * GB
const aiTokensQuota = 100000

export const profileSchema = baseEntitySchema.extend({
  id: z.string(),
  type: z.literal('profile'),
  userId: z.string(),
  email: z.string(),
  name: z.string().optional(),
  /** URL of a picture for the user. Child images does take precedence, and is handled by the profiles API. */
  picture: z.union([z.string(), z.null()]).optional(),
  /** Profile color */
  color: z.enum(colorNames).optional(),

  /** Subscriber ID */
  sub: z.string().optional(),

  /**
   * User's current usage for the limits we enforce
   * @important We do DynamoDB atomicMath on these fields (not type checked) so careful with changing them
   */
  usage: z
    .object({
      storage: z.number().default(0),
      aiTokens: z.number().default(0),
    })
    .optional()
    .default({
      storage: 0,
      aiTokens: 0,
    }),
  /** User's quota */

  quota: z
    .object({
      // The maximum storage the user can use
      storage: z.number().default(storageQuota),
      // The maximum number of tokens the user can use
      aiTokens: z.number().default(aiTokensQuota),
    })
    .default({
      storage: storageQuota,
      aiTokens: aiTokensQuota,
    }),

  state: z
    .object({
      // The user has an initialized account, e.g., with starter content
      initialized: z.boolean().default(false),
    })
    .optional()
    .default({ initialized: false }),

  /**
   * User configuration. useUserConfig() should be used to access this.
   */
  config: z.record(z.string(), z.unknown()).optional().default({}),
})

export type Profile = z.infer<typeof profileSchema>

export function isProfile(node: unknown): node is Profile {
  return profileSchema.safeParse(node).success
}

export const publicProfileSchema = profileSchema.pick({
  id: true,
  type: true,
  userId: true,
  name: true,
  email: true,
  picture: true,
  color: true,
})
export type PublicProfile = z.infer<typeof publicProfileSchema>

export default profileSchema
